import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Palette, CreditCard, Puzzle, Users, FileSignature, CheckCircle2, CalendarDays, Mail } from "lucide-react";
import { api } from "@/lib/api";
import HiringCompassCard from "@/components/HiringCompassCard";

export default function Settings() {
  const { user } = useAuth();
  const [ds, setDs] = useState(null);
  const [g, setG] = useState(null);
  const [email, setEmail] = useState(null);

  useEffect(() => {
    api.get("/docusign/status").then(r => setDs(r.data)).catch(() => setDs(null));
    api.get("/integrations/status").then(r => setG(r.data)).catch(() => setG(null));
    api.get("/recruit/email/status").then(r => setEmail(r.data)).catch(() => setEmail(null));
  }, []);

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500">Settings</div>
        <h1 className="text-3xl font-bold mt-1">Workspace settings</h1>
        <p className="text-neutral-600 mt-1">Manage branding, integrations, permissions and billing.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm"><CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2"><Building2 className="w-4 h-4 text-pink" /><h3 className="font-semibold">Company</h3></div>
          <div className="text-sm text-neutral-600">Name: <span className="font-medium">Alfred Demo Co.</span></div>
          <div className="text-sm text-neutral-600">Org ID: <span className="font-mono text-xs">{user?.org_id}</span></div>
        </CardContent></Card>

        <Card className="border-0 shadow-sm"><CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2"><Palette className="w-4 h-4 text-pink" /><h3 className="font-semibold">Branding</h3></div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-pink" title="Primary" />
            <div className="w-10 h-10 rounded-lg bg-teal" title="Success" />
            <div className="w-10 h-10 rounded-lg bg-navy" title="Navy" />
          </div>
          <div className="text-xs text-neutral-500 mt-2">White‑label tokens per design system.</div>
        </CardContent></Card>

        <div className="md:col-span-2">
          <HiringCompassCard />
        </div>

        <Card className="border-0 shadow-sm md:col-span-2" data-testid="google-integrations-card">          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-pink" />
                <h3 className="font-semibold">Google Calendar & Gmail</h3>
              </div>
              {g ? (
                <Badge className={g.google_calendar?.configured ? "bg-teal text-white border-0" : "bg-amber-500 text-white border-0"} data-testid="google-status-badge">
                  {g.google_calendar?.configured ? "Connected" : "Not connected — using .ics export + demo email"}
                </Badge>
              ) : <Badge variant="outline">Checking…</Badge>}
            </div>
            <p className="text-sm text-neutral-600">
              Two-way sync interviews to Google Calendar and send invitations from Gmail. Meanwhile, every scheduled
              interview offers a downloadable <span className="font-mono text-xs">.ics</span> that works with Google, Outlook, and Apple Calendar.
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-2 text-xs font-mono">
              {(g?.google_calendar?.required_env || ["GOOGLE_CLIENT_ID","GOOGLE_CLIENT_SECRET","GOOGLE_REDIRECT_URI"]).map(k => (
                <div key={k} className="flex items-center gap-2 rounded-md border px-3 py-2">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="truncate">{k}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <div className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs">
                <CalendarDays className="w-3.5 h-3.5 text-pink" /> Google Calendar
                <Badge className="ml-1 bg-amber-100 text-amber-800 border-0 text-[9px]">Coming soon</Badge>
              </div>
              <div className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs">
                <Mail className="w-3.5 h-3.5 text-pink" /> Gmail
                <Badge className="ml-1 bg-amber-100 text-amber-800 border-0 text-[9px]">Coming soon</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm md:col-span-2" data-testid="email-settings-card">          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-pink" />
                <h3 className="font-semibold">Email inbox (Gmail / Outlook)</h3>
              </div>
              {email ? (
                <Badge className={email.configured ? "bg-teal text-white border-0" : "bg-amber-500 text-white border-0"} data-testid="email-status-badge">
                  {email.configured ? `Connected · ${email.provider || "SMTP"} · ${email.sender_email}` : "Not connected — links copy to clipboard instead"}
                </Badge>
              ) : <Badge variant="outline">Checking…</Badge>}
            </div>
            <p className="text-sm text-neutral-600">
              Connect your Gmail or Outlook inbox to send reference-check invites and behavioural assessment invitations directly.
              Alfred sends via SMTP using your own address, so replies come back to your inbox naturally.
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-2 text-xs font-mono">
              {["SMTP_USER", "SMTP_PASSWORD", "SMTP_PROVIDER (or SMTP_HOST/SMTP_PORT)", "SENDER_EMAIL (optional)"].map(k => (
                <div key={k} className="flex items-center gap-2 rounded-md border px-3 py-2">
                  {email?.configured ? <CheckCircle2 className="w-3.5 h-3.5 text-teal" /> : <span className="w-2 h-2 rounded-full bg-neutral-300" />}
                  <span className="truncate">{k}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-neutral-500 space-y-1">
              <div><span className="font-semibold text-neutral-700">Gmail:</span> Set <span className="font-mono">SMTP_PROVIDER=gmail</span> and use a <a className="text-pink hover:underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">Google App Password</a> as <span className="font-mono">SMTP_PASSWORD</span> (2FA must be on).</div>
              <div><span className="font-semibold text-neutral-700">Outlook / Office 365:</span> Set <span className="font-mono">SMTP_PROVIDER=outlook</span> and use your mailbox password (or app password if 2FA is on).</div>
              <div><span className="font-semibold text-neutral-700">Custom SMTP:</span> Provide <span className="font-mono">SMTP_HOST</span>, <span className="font-mono">SMTP_PORT</span> directly.</div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm md:col-span-2" data-testid="docusign-settings-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileSignature className="w-4 h-4 text-pink" />
                <h3 className="font-semibold">DocuSign eSignature</h3>
              </div>
              {ds ? (
                <Badge className={ds.configured ? "bg-teal text-white border-0" : "bg-amber-500 text-white border-0"} data-testid="docusign-status-badge">
                  {ds.configured ? "Configured" : "Not configured — using inline signing"}
                </Badge>
              ) : <Badge variant="outline">Checking…</Badge>}
            </div>
            <p className="text-sm text-neutral-600">
              Alfred can send real DocuSign envelopes for e‑signature tasks. When the customer provides an integrator key,
              set the required env vars below and Alfred will automatically switch from inline canvas signing to a real
              DocuSign envelope flow.
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-2 text-xs font-mono">
              {(ds?.required_env || ["DS_CLIENT_ID","DS_IMPERSONATED_USER_ID","DS_ACCOUNT_ID","DS_PRIVATE_KEY_PATH","DS_REDIRECT_URI"]).map(k => {
                const missing = ds?.missing?.includes(k);
                return (
                  <div key={k} className="flex items-center gap-2 rounded-md border px-3 py-2">
                    {missing ? <span className="w-2 h-2 rounded-full bg-red-500" /> : <CheckCircle2 className="w-3.5 h-3.5 text-teal" />}
                    <span className="truncate">{k}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-neutral-500 mt-3">
              Grant consent once via the DocuSign consent URL, then place the RSA private key at
              <span className="font-mono ml-1">DS_PRIVATE_KEY_PATH</span>. Environment: <span className="font-mono">{ds?.env || "sandbox"}</span>.
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm"><CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2"><Puzzle className="w-4 h-4 text-pink" /><h3 className="font-semibold">Integrations</h3></div>
          <div className="flex flex-wrap gap-2 text-xs">
            {["Google Workspace","Slack","Xero","Employment Hero","Jamf","LinkedIn","Seek","Whisper"].map(i => (
              <Badge key={i} variant="outline">{i}</Badge>
            ))}
          </div>
        </CardContent></Card>

        <Card className="border-0 shadow-sm"><CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-pink" /><h3 className="font-semibold">Roles & permissions</h3></div>
          <div className="text-sm text-neutral-600">Your role: <Badge className="bg-pink text-white border-0">{user?.role}</Badge></div>
        </CardContent></Card>

        <Card className="border-0 shadow-sm md:col-span-2"><CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2"><CreditCard className="w-4 h-4 text-pink" /><h3 className="font-semibold">Billing</h3></div>
          <div className="text-sm text-neutral-600">Growth plan · 2,000 AI credits/month · <span className="text-teal">Trial (14 days)</span></div>
        </CardContent></Card>
      </div>
    </div>
  );
}
